import { Component, Input, SimpleChanges } from '@angular/core';
import { ChartData, DataDepth } from '../../../../../shared/models/depth-data';
import { TranslateService } from '@ngx-translate/core';
import { Chart } from 'chart.js';
import { GlobalVars } from '../../../../../shared/global-vars';
import { DataResult } from '../../../../../shared/models/chart-forecast-data';
import { subDays } from 'date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';

@Component({
  selector: 'app-chart-mobile',
  templateUrl: './chart-mobile.component.html',
  styleUrl: './chart-mobile.component.scss'
})
export class ChartMobileComponent {
  @Input() chartData: ChartData | null = null;
  chart: any;
  colors = GlobalVars.colors;
  endDate: Date;
  startDate: Date;
  periodDays: number = 31;
  maxYDepth: number = 0;
  minYDepth: number = 0;

  constructor(private translate: TranslateService) {
    this.endDate = new Date();
    this.startDate = subDays(this.endDate, this.periodDays);
  }

  ngOnInit(): void {
    Chart.register(zoomPlugin);

    if (this.chartData) {
      this.updateChart();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartData'] && this.chartData && this.chartData.result) {
      this.updateChart();
    }
  }

  updateChart() {
    if (!this.chartData) {
      return; // Ne pas essayer de créer le graphique si les données ne sont pas prêtes
    }

    const startDate = this.startDate;
    const endDate = this.endDate;
    this.drawChart({ startDate, endDate });
  }

  drawChart(range?: { startDate: Date; endDate: Date }) {
    if (this.chart) {
      this.chart.destroy();
    }

    const datasets = this.getDatasets(range);
    
    let ctx = document.getElementById('chart') as HTMLCanvasElement;

    if (!ctx) {
      console.error('Failed to acquire chart context');
      return;
    }

    const yMin = this.getYAxisMin(this.chartData?.result);

    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: false,
            text: "",
            font: {
              size: 18
            }
          },
          legend: {
            display: false
          },
          zoom: {
            zoom: {
              wheel: {
                enabled: true,
              },
              pinch: {
                enabled: true,
              },
              mode: 'xy'
            },
            pan: {
              enabled: true
            },
            limits: {
              x: { min: 'original', max: 'original' },
              y: { min: yMin, max: 'original' }
            }
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (
                  context.raw &&
                  typeof (context.raw as any).y === 'number' &&
                  context.dataset.label === this.translate.instant('HEADBAND.MODELE.MODAL.GRAPH.DEPTH_ALTITUDE')
                ) {
                  const depthAltitudeText = this.translate.instant('HEADBAND.MODELE.MODAL.GRAPH.DEPTH_ALTITUDE');
                  return `${depthAltitudeText}: ${(context.raw as any).y}`;
                }
                return '';
              }
            }
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        },
        scales: {
          x: {
            display: false,
            type: 'time',
            time: {
              unit: 'day',
              tooltipFormat: 'dd/MM/yyyy HH:mm:ss',
              displayFormats: {
                day: 'dd/MM/yyyy'
              }
            },
            title: {
              display: true,
              text: ''
            },
            ticks: {
              font: {
                size: 10
              }
            },
            stacked: true
          },
          y: {
            display: false,
            title: {
              display: true,
              text: ''
            },
            min: yMin,
            max: this.maxYDepth,
            stacked: true
          },
        },
        layout: {
          padding: 0
        }
      }
    });
  }

  getYAxisMin(data: DataDepth[] | undefined) {
    if (!data || data.length === 0) return this.maxYDepth;
    const values = data.map(d => d.value);
    const maxDepthValue = Math.max(...values);
    const margin = maxDepthValue * 0.1;
    return this.maxYDepth - (maxDepthValue + margin);
  }

  getDatasets(range?: { startDate: Date; endDate: Date }) {
    
    let allDatasets: any = [];

    if(this.chartData?.typeData === "depth") {
      allDatasets.push(this.createDataset(this.translate.instant('HEADBAND.MODELE.MODAL.GRAPH.DEPTH'), this.chartData.result, this.colors.depth, range));
      allDatasets.push(this.createDataset(this.translate.instant('HEADBAND.MODELE.MODAL.GRAPH.DEPTH_ALTITUDE'), this.chartData.result, this.colors.depth_altitude, range));
    }

    return allDatasets;
  }

  createDataset(label: string, data: DataDepth[] | undefined, color: string, range?: { startDate: Date; endDate: Date }) {
    if (data && data.length > 0) {
      const filteredData = range
      ? data.filter(d => {
          const date = new Date(d.date);
          return date >= range.startDate && date <= range.endDate;
        })
      : data;

      if(data[0]?.altitudeRepere){
        this.maxYDepth = data[0].altitudeRepere;
      }

      let borderDash: number[] = [];
      let type: string = 'line';
      let dataReturn: any = filteredData.map(d => ({ x: new Date(d.date), y: d.value }));
      let stepped: boolean = false;
      let hoverBackgroundColor: string = "#FFFFFF";
      let backgroundColor: string = this.hexToRgba("#FFFFFF", 0.25);
      let fill: boolean = false;
      let borderWidth: number = 2;
      let hidden: boolean = false;

      if (this.chartData?.typeData === "depth"){
        if (label == this.translate.instant('HEADBAND.MODELE.MODAL.GRAPH.DEPTH_ALTITUDE')){
          dataReturn = filteredData.map(d => ({ x: new Date(d.date), y: d.value }));
          backgroundColor = this.hexToRgba(color, 0.20);
        } else if (label == this.translate.instant('HEADBAND.MODELE.MODAL.GRAPH.DEPTH')){
          dataReturn = filteredData.map(d => ({ x: new Date(d.date), y: d.altitudeRepere - d.value }));
          backgroundColor = this.hexToRgba(color, 0.50);
        }
        
        type = 'bar';
        hoverBackgroundColor = color;
        
        borderWidth = 0.5;
      }

      return {
        type: type,
        label: label,
        data: dataReturn,
        borderColor: '#FFFFFF',
        backgroundColor: backgroundColor,
        borderWidth: borderWidth,
        fill: fill,
        hidden: hidden,
        pointRadius: 0.2,
        borderDash: borderDash,
        tension: 0.1,
        stepped: stepped,
        categoryPercentage: 1.0,
        barPercentage: 1.0
      };
    }
    else {
      return {};
    }
  }

  hexToRgba(hex: string, alpha: number): string {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}
